/**
 * Avaliador de `RuleExpr` — TypeScript puro, sem Angular e sem executar código.
 *
 * É a peça que substitui o `new Function()` removido na Fase 0. Toda condição da
 * tela passa por aqui: `visibleWhen`, `disabledWhen`, `requiredWhen` e o `when`
 * de cada evento.
 *
 * Três decisões que valem ser explícitas, porque mudam o comportamento em
 * produção:
 *
 * 1. **Não lança.** Uma tela publicada não pode quebrar porque uma regra aponta
 *    para um campo que alguém removeu no editor. Regra que não dá para avaliar
 *    devolve `false` e registra o problema em `onIssue`. Falhar fechado é a
 *    escolha certa aqui: campo condicional que não aparece é um bug visível;
 *    campo que aparece quando não devia pode ser vazamento de informação.
 *
 * 2. **Referência órfã é reportada, não engolida.** `resolve` devolve
 *    `{ found, value }` justamente para separar "o campo existe e está vazio" de
 *    "o campo não existe". Hoje isso falha em silêncio, e é um dos erros que a
 *    tela "Verificar tela" precisa listar.
 *
 * 3. **Comparação tolerante a tipo, de propósito.** Valor de formulário chega
 *    como string quase sempre (`'10'`, `'true'`, `'2025-01-15'`). Exigir tipo
 *    exato faria toda regra numérica falhar em silêncio, que é pior que
 *    converter. As conversões estão em `coerceForComparison`, uma só função,
 *    para o comportamento ser auditável num lugar.
 */
import { RuleComparison, RuleExpr, RuleOperand } from '../model/behavior';

/** Profundidade máxima de aninhamento. Rede contra JSON malformado ou cíclico. */
export const MAX_RULE_DEPTH = 20;

/**
 * Tamanho máximo de um padrão de `matches`.
 *
 * O padrão vem do banco e roda no navegador do usuário. Não há como interromper
 * uma expressão regular em JavaScript depois que ela começa, então um padrão com
 * retrocesso catastrófico trava a aba. O limite de tamanho reduz a superfície,
 * mas NÃO elimina o risco — ver a nota de segurança na seção 8.3 do plano.
 */
export const MAX_PATTERN_LENGTH = 200;

/** O que a avaliação encontrou de errado no caminho. */
export interface RuleIssue {
  readonly kind:
    | 'unknownPath'      // a regra aponta para campo que não existe
    | 'missingOperand'   // comparação sem o lado direito
    | 'notComparable'    // os dois lados não são comparáveis entre si
    | 'badPattern'       // regex inválida ou longa demais
    | 'unknownOperator'
    | 'tooDeep';
  readonly detail: string;
}

/** Resultado da busca de um caminho no estado da tela. */
export interface ResolvedValue {
  /** `false` quando o caminho não existe — referência órfã. */
  readonly found: boolean;
  readonly value: unknown;
}

export interface RuleContext {
  /** Busca o valor atual de um caminho. Nunca deve lançar. */
  readonly resolve: (path: string) => ResolvedValue;
  /** Recebe cada problema encontrado. Opcional. */
  readonly onIssue?: (issue: RuleIssue) => void;
}

// ---------------------------------------------------------------- entrada

/**
 * Avalia a expressão. Ausente significa "sem condição", que é verdadeiro —
 * é o que faz `visibleWhen` indefinido querer dizer "sempre visível".
 */
export function evaluateRule(expr: RuleExpr | undefined, context: RuleContext): boolean {
  if (expr === undefined || expr === null) {
    return true;
  }
  return evaluateAtDepth(expr, context, 0);
}

function evaluateAtDepth(expr: RuleExpr, context: RuleContext, depth: number): boolean {
  if (depth > MAX_RULE_DEPTH) {
    report(context, 'tooDeep', 'aninhamento acima de ' + MAX_RULE_DEPTH + ' níveis');
    return false;
  }

  if (expr === null || typeof expr !== 'object') {
    report(context, 'unknownOperator', 'expressão não é um objeto');
    return false;
  }

  // Checagem por FORMA, não pela etiqueta. A expressão vem do banco, e um JSON
  // com `op: 'and'` mas sem `operands` não pode derrubar a tela.
  if (isGroup(expr)) {
    if (expr.op === 'and') {
      return evaluateAnd(expr.operands, context, depth);
    }
    return evaluateOr(expr.operands, context, depth);
  }

  if (isNegation(expr)) {
    return !evaluateAtDepth(expr.operand, context, depth + 1);
  }

  if (isComparison(expr)) {
    return evaluateComparison(expr.op, expr.left, expr.right, context);
  }

  report(context, 'unknownOperator', 'expressão sem forma reconhecível');
  return false;
}

type GroupExpr = { readonly op: 'and' | 'or'; readonly operands: readonly RuleExpr[] };
type NegationExpr = { readonly op: 'not'; readonly operand: RuleExpr };
type ComparisonExpr = {
  readonly op: RuleComparison;
  readonly left: RuleOperand;
  readonly right?: RuleOperand;
};

function isGroup(expr: RuleExpr): expr is GroupExpr {
  const candidate = expr as Partial<GroupExpr>;
  if (candidate.op !== 'and' && candidate.op !== 'or') {
    return false;
  }
  return Array.isArray(candidate.operands);
}

function isNegation(expr: RuleExpr): expr is NegationExpr {
  const candidate = expr as Partial<NegationExpr>;
  if (candidate.op !== 'not') {
    return false;
  }
  return candidate.operand !== undefined && candidate.operand !== null;
}

function isComparison(expr: RuleExpr): expr is ComparisonExpr {
  const candidate = expr as Partial<ComparisonExpr>;
  if (typeof candidate.op !== 'string') {
    return false;
  }
  return candidate.left !== undefined && candidate.left !== null;
}

/**
 * `and` sem operandos é verdadeiro, e `or` sem operandos é falso — as
 * convenções usuais. Vale registrar porque o editor pode gerar um grupo vazio
 * enquanto o usuário monta a condição, e nesse meio-tempo a tela não deve piscar.
 */
function evaluateAnd(operands: readonly RuleExpr[], context: RuleContext, depth: number): boolean {
  if (!Array.isArray(operands) || operands.length === 0) {
    return true;
  }

  for (const operand of operands) {
    if (!evaluateAtDepth(operand, context, depth + 1)) {
      return false;
    }
  }

  return true;
}

function evaluateOr(operands: readonly RuleExpr[], context: RuleContext, depth: number): boolean {
  if (!Array.isArray(operands) || operands.length === 0) {
    return false;
  }

  for (const operand of operands) {
    if (evaluateAtDepth(operand, context, depth + 1)) {
      return true;
    }
  }

  return false;
}

// ---------------------------------------------------------------- comparacoes

function evaluateComparison(
  op: RuleComparison,
  left: RuleOperand,
  right: RuleOperand | undefined,
  context: RuleContext
): boolean {

  const leftValue = readOperand(left, context);

  // `empty` e `notEmpty` são os únicos de um operando só.
  if (op === 'empty') {
    return isEmptyValue(leftValue);
  }
  if (op === 'notEmpty') {
    return !isEmptyValue(leftValue);
  }

  if (right === undefined) {
    report(context, 'missingOperand', 'operador "' + op + '" sem o lado direito');
    return false;
  }

  if (op === 'in') {
    return evaluateIn(leftValue, right, context);
  }

  const rightValue = readOperand(right, context);

  if (op === 'eq') {
    return valuesAreEqual(leftValue, rightValue);
  }
  if (op === 'neq') {
    return !valuesAreEqual(leftValue, rightValue);
  }

  if (op === 'gt' || op === 'gte' || op === 'lt' || op === 'lte') {
    return compareOrdered(op, leftValue, rightValue, context);
  }

  if (op === 'contains' || op === 'startsWith' || op === 'endsWith') {
    return compareText(op, leftValue, rightValue);
  }

  if (op === 'matches') {
    return matchesPattern(leftValue, rightValue, context);
  }

  report(context, 'unknownOperator', 'operador desconhecido: ' + String(op));
  return false;
}

/**
 * `in` é o único que aceita o operando `list` do lado direito, e por isso não
 * passa por `readOperand` — a lista inteira é o valor, não um item dela.
 */
function evaluateIn(leftValue: unknown, right: RuleOperand, context: RuleContext): boolean {
  let candidates: readonly unknown[];

  if ('list' in right && Array.isArray(right.list)) {
    candidates = right.list;
  } else {
    const rightValue = readOperand(right, context);
    if (Array.isArray(rightValue)) {
      candidates = rightValue;
    } else {
      report(context, 'notComparable', 'operador "in" exige uma lista do lado direito');
      return false;
    }
  }

  for (const candidate of candidates) {
    if (valuesAreEqual(leftValue, candidate)) {
      return true;
    }
  }

  return false;
}

function compareOrdered(
  op: 'gt' | 'gte' | 'lt' | 'lte',
  leftValue: unknown,
  rightValue: unknown,
  context: RuleContext
): boolean {

  const left = coerceForComparison(leftValue);
  const right = coerceForComparison(rightValue);

  let difference: number;

  if (left.kind === 'number' && right.kind === 'number') {
    difference = left.asNumber - right.asNumber;
  } else if (left.kind === 'date' && right.kind === 'date') {
    difference = left.asNumber - right.asNumber;
  } else if (left.kind === 'text' || right.kind === 'text') {
    // Comparação de texto, sem diferenciar acento nem caixa — 'Ana' < 'Bruno'.
    difference = left.asText.localeCompare(right.asText, 'pt-BR', { sensitivity: 'base' });
  } else {
    report(context, 'notComparable',
      'não dá para ordenar ' + left.kind + ' com ' + right.kind);
    return false;
  }

  if (op === 'gt') {
    return difference > 0;
  }
  if (op === 'gte') {
    return difference >= 0;
  }
  if (op === 'lt') {
    return difference < 0;
  }
  return difference <= 0;
}

/** Texto sem diferenciar caixa: regra de negócio não deveria depender disso. */
function compareText(
  op: 'contains' | 'startsWith' | 'endsWith',
  leftValue: unknown,
  rightValue: unknown
): boolean {

  const haystack = toComparableText(leftValue).toLowerCase();
  const needle = toComparableText(rightValue).toLowerCase();

  if (needle.length === 0) {
    // Todo texto contém a string vazia, e começa e termina com ela.
    return true;
  }

  if (op === 'contains') {
    return haystack.indexOf(needle) >= 0;
  }
  if (op === 'startsWith') {
    return haystack.startsWith(needle);
  }
  return haystack.endsWith(needle);
}

function matchesPattern(leftValue: unknown, rightValue: unknown, context: RuleContext): boolean {
  const pattern = toComparableText(rightValue);

  if (pattern.length === 0) {
    report(context, 'badPattern', 'padrão vazio');
    return false;
  }

  if (pattern.length > MAX_PATTERN_LENGTH) {
    report(context, 'badPattern',
      'padrão com ' + pattern.length + ' caracteres, acima do limite de ' + MAX_PATTERN_LENGTH);
    return false;
  }

  let expression: RegExp;
  try {
    // Sem sinalizadores vindos de fora: o padrão é só o corpo.
    expression = new RegExp(pattern);
  } catch {
    report(context, 'badPattern', 'expressão regular inválida: ' + pattern);
    return false;
  }

  return expression.test(toComparableText(leftValue));
}

// ---------------------------------------------------------------- valores

function readOperand(operand: RuleOperand, context: RuleContext): unknown {
  if (operand === null || typeof operand !== 'object') {
    return undefined;
  }

  if ('path' in operand) {
    const resolved = context.resolve(operand.path);
    if (!resolved.found) {
      report(context, 'unknownPath', 'a regra aponta para "' + operand.path + '", que não existe');
    }
    return resolved.value;
  }

  if ('value' in operand) {
    return operand.value;
  }

  if ('list' in operand) {
    return operand.list;
  }

  return undefined;
}

/**
 * Vazio para efeito de regra: nulo, indefinido, texto só com espaço, e lista sem
 * itens. `0` e `false` NÃO são vazios — confundir os dois é o erro clássico, e
 * numa tela de negócio "quantidade igual a zero" precisa continuar sendo um
 * valor preenchido.
 */
function isEmptyValue(value: unknown): boolean {
  if (value === null || value === undefined) {
    return true;
  }
  if (typeof value === 'string') {
    return value.trim().length === 0;
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return false;
}

function valuesAreEqual(leftValue: unknown, rightValue: unknown): boolean {
  // Vazio é igual a vazio, qualquer que seja a forma — o campo não preenchido
  // chega ora como null, ora como '' , dependendo do widget.
  const leftEmpty = isEmptyValue(leftValue);
  const rightEmpty = isEmptyValue(rightValue);
  if (leftEmpty || rightEmpty) {
    return leftEmpty && rightEmpty;
  }

  const left = coerceForComparison(leftValue);
  const right = coerceForComparison(rightValue);

  if (left.kind === 'boolean' || right.kind === 'boolean') {
    return left.asBoolean === right.asBoolean;
  }
  if (left.kind === 'number' && right.kind === 'number') {
    return left.asNumber === right.asNumber;
  }
  if (left.kind === 'date' && right.kind === 'date') {
    return left.asNumber === right.asNumber;
  }

  return left.asText.localeCompare(right.asText, 'pt-BR', { sensitivity: 'base' }) === 0;
}

type ComparableKind = 'number' | 'date' | 'boolean' | 'text';

interface ComparableValue {
  readonly kind: ComparableKind;
  readonly asNumber: number;
  readonly asBoolean: boolean;
  readonly asText: string;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}([T ]\d{2}:\d{2}(:\d{2})?)?/;
const BR_DATE = /^(\d{2})\/(\d{2})\/(\d{4})$/;

/**
 * Traduz um valor para algo comparável, num só lugar.
 *
 * A ordem importa: booleano antes de número (senão `true` viraria `NaN`), e data
 * antes de texto (senão '2025-01-15' compararia como string, o que por acaso
 * funciona em ISO mas não em DD/MM/AAAA).
 */
function coerceForComparison(value: unknown): ComparableValue {
  if (typeof value === 'boolean') {
    return { kind: 'boolean', asNumber: value ? 1 : 0, asBoolean: value, asText: String(value) };
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return { kind: 'number', asNumber: value, asBoolean: value !== 0, asText: String(value) };
  }

  if (value instanceof Date) {
    const time = value.getTime();
    return { kind: 'date', asNumber: time, asBoolean: true, asText: value.toISOString() };
  }

  const text = toComparableText(value);

  if (text === 'true' || text === 'false') {
    const asBoolean = text === 'true';
    return { kind: 'boolean', asNumber: asBoolean ? 1 : 0, asBoolean, asText: text };
  }

  const asDate = parseDateText(text);
  if (asDate !== null) {
    return { kind: 'date', asNumber: asDate, asBoolean: true, asText: text };
  }

  const asNumber = parseNumberText(text);
  if (asNumber !== null) {
    return { kind: 'number', asNumber, asBoolean: asNumber !== 0, asText: text };
  }

  return { kind: 'text', asNumber: Number.NaN, asBoolean: text.length > 0, asText: text };
}

function parseDateText(text: string): number | null {
  if (ISO_DATE.test(text)) {
    const parsed = Date.parse(text);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  const brazilian = BR_DATE.exec(text);
  if (brazilian !== null) {
    const day = Number(brazilian[1]);
    const month = Number(brazilian[2]);
    const year = Number(brazilian[3]);
    const parsed = Date.UTC(year, month - 1, day);
    if (!Number.isNaN(parsed)) {
      return parsed;
    }
  }

  return null;
}

/**
 * Só converte texto que é INTEIRAMENTE numérico. Sem isto, '12 unidades'
 * viraria 12 e uma regra passaria a valer sem ninguém entender por quê.
 */
function parseNumberText(text: string): number | null {
  if (text.length === 0) {
    return null;
  }
  if (!/^[+-]?\d+([.,]\d+)?$/.test(text)) {
    return null;
  }

  const normalized = text.replace(',', '.');
  const parsed = Number(normalized);

  if (Number.isFinite(parsed)) {
    return parsed;
  }
  return null;
}

function toComparableText(value: unknown): string {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'string') {
    return value.trim();
  }
  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  if (Array.isArray(value)) {
    return value.map(item => toComparableText(item)).join(',');
  }
  return '';
}

function report(context: RuleContext, kind: RuleIssue['kind'], detail: string): void {
  if (context.onIssue !== undefined) {
    context.onIssue({ kind, detail });
  }
}

// ---------------------------------------------------------------- dependencias

/**
 * Todos os caminhos que a expressão lê.
 *
 * Serve a dois usos, e ambos são obrigatórios:
 *
 *  - **saber quando reavaliar**: `visibleWhen` só precisa rodar de novo quando um
 *    dos campos que ele lê muda. Sem esta lista, a alternativa é reavaliar toda
 *    regra da tela a cada tecla digitada;
 *  - **achar referência órfã** na tela "Verificar tela", sem esperar a regra ser
 *    avaliada em produção para descobrir que aponta para um campo removido.
 */
export function collectRulePaths(expr: RuleExpr | undefined): readonly string[] {
  const paths: string[] = [];
  collectInto(expr, paths, 0);
  return paths;
}

function collectInto(expr: RuleExpr | undefined, paths: string[], depth: number): void {
  if (expr === undefined || expr === null || typeof expr !== 'object') {
    return;
  }
  if (depth > MAX_RULE_DEPTH) {
    return;
  }

  if (isGroup(expr)) {
    for (const operand of expr.operands) {
      collectInto(operand, paths, depth + 1);
    }
    return;
  }

  if (isNegation(expr)) {
    collectInto(expr.operand, paths, depth + 1);
    return;
  }

  if (isComparison(expr)) {
    addOperandPath(expr.left, paths);
    addOperandPath(expr.right, paths);
  }
}

function addOperandPath(operand: RuleOperand | undefined, paths: string[]): void {
  if (operand === undefined || operand === null || typeof operand !== 'object') {
    return;
  }
  if (!('path' in operand)) {
    return;
  }
  if (paths.indexOf(operand.path) >= 0) {
    return;
  }
  paths.push(operand.path);
}
